import { useState, useCallback, useEffect } from 'react'
import { saveDocPage } from '../../../services/api'

/**
 * Génère un slug URL-safe à partir d'un titre.
 */
export const generateSlug = (title) =>
  title
    .toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    || `page-${Date.now()}`

/**
 * Lister les pages du builder, en créer, en dupliquer.
 *
 * Source de vérité : `GET /api/builder/pages`. Deux populations DISJOINTES y
 * cohabitent, jamais mélangées : les pages ordinaires (par défaut) et les
 * MODÈLES (`?templates=1`) — une page marquée modèle n'est servie nulle part,
 * elle n'a donc rien à faire dans une liste de pages.
 *
 * `siteId` cloisonne les deux listes par site : c'est la portée des modèles,
 * ceux d'un site n'étant proposés que là. Le back le résout par la relation de
 * groupe (il n'y a pas de champ « site » sur un contenu).
 *
 * `directoryId` bascule le hook sur le WIKI — une page documentaire est une
 * `builder_page` rangée dans un répertoire (`field_directory`), et ses écrans de
 * création sont les MÊMES que ceux d'un site. Deux différences irréductibles,
 * qui tiennent aux droits :
 *  - `/api/builder/pages` exige `use site builder`, que les rédacteurs du wiki
 *    n'ont pas : la liste des modèles vient de l'API decoupled générique, déjà
 *    filtrée par les node grants du répertoire ;
 *  - la CRÉATION passe par `/decoupled-api/save?…&directory=<tid>`, seul chemin
 *    où `orbit_doc_node_create_access()` peut lire le répertoire cible et
 *    statuer sur `can_create_children` (cf. BuilderWriteAccess::save, qui refuse
 *    explicitement les créations de pages wiki).
 */
export const useBuilderPages = (siteId = null, directoryId = null) => {
  const [pages, setPages] = useState([])
  const [templates, setTemplates] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const loadPages = useCallback(async () => {
    setLoading(true)
    setError(null)
    const site = siteId ? `&site=${encodeURIComponent(siteId)}` : ''
    try {
      if (directoryId) {
        // Les modèles du WIKI. Le drapeau `field_is_template` est porté aussi
        // par les modèles de sites : seul le répertoire racine (posé au presave
        // sur une page rangée, absent d'une page de site) les distingue. L'API
        // refusant une valeur vide, « >= 1 » tient lieu d'`IS NOT NULL`.
        const res = await fetch(
          '/decoupled-api/node/builder_page/collection?display=decoupled'
          + '&filter[0][f]=field_is_template&filter[0][v]=1'
          + '&filter[1][f]=field_root_directory&filter[1][v]=1&filter[1][c]=%3E%3D'
          + '&sort[0][f]=title&sort[0][d]=ASC',
          { credentials: 'include' },
        )
        const rows = res.ok ? await res.json() : []
        setPages([])
        setTemplates((Array.isArray(rows) ? rows : []).map((item) => {
          const d = item?.data ?? item
          return {
            nid: parseInt(d.id, 10),
            slug: d.slug || '',
            title: d.title || 'Sans titre',
            description: d.summary || '',
          }
        }).filter((t) => t.slug))
        return
      }
      const [pagesRes, templatesRes] = await Promise.all([
        fetch(`/api/builder/pages?_=${Date.now()}${site}`, { credentials: 'include' }),
        fetch(`/api/builder/pages?templates=1&_=${Date.now()}${site}`, { credentials: 'include' }),
      ])
      setPages(pagesRes.ok ? await pagesRes.json() : [])
      // Les modèles sont un confort : leur absence laisse « page vierge »
      // parfaitement utilisable, elle ne doit pas faire échouer l'écran.
      setTemplates(templatesRes.ok ? await templatesRes.json() : [])
    } catch (err) {
      setError(err)
      setPages([])
      setTemplates([])
    } finally {
      setLoading(false)
    }
  }, [siteId, directoryId])

  useEffect(() => { loadPages() }, [loadPages])

  /**
   * Crée une page VIERGE via POST /api/builder/save.
   *
   * Aucun bloc n'est posé d'office : l'habillage d'un site (header, footer) vient
   * de son MODÈLE, pas d'une liste codée en dur — jusqu'au 2026-09-02, ce hook
   * injectait un `DemocratsHeader` (logo democrats.eu compris) et un
   * `DemocratsFooter` dans toute page créée, quel que soit le site.
   *
   * `siteId` (optionnel) : le gid du site auquel rattacher la page d'emblée.
   * C'est la clé `siteId` du save, que Drupal applique APRÈS l'enregistrement —
   * une relation de groupe a besoin d'un nid (cf. OrbitBuilderController::save).
   * Le faire dans le MÊME appel évite la page orpheline qu'un échec laisserait.
   *
   * `isTemplate` : crée directement un MODÈLE (geste « créer un modèle »).
   *
   * @returns {string} slug de la page créée
   */
  const createPage = useCallback(async (title, blocs = [], targetSiteId = null, isTemplate = false) => {
    const slug = generateSlug(title)

    // AUCUNE validation d'URI ici : créer une page ne doit pas échouer sur son
    // adresse. La clé `create` dit au back qu'il CRÉE — il unifie alors le slug
    // par suffixe (`contact-2`) au lieu de résoudre la page qui le porte déjà et
    // d'en écraser le contenu. L'adresse définitive se règle ensuite dans le
    // panneau « Adresse », où un verdict de disponibilité a du sens.
    const pageData = {
      slug,
      title,
      create: true,
      isTemplate,
      // Clé ABSENTE quand aucun site n'est demandé : le save ne touche au
      // rattachement que si elle est présente — un appelant qui l'ignore ne
      // détache donc rien à son insu.
      ...(targetSiteId ? { siteId: Number(targetSiteId) } : {}),
      blocs: blocs.map(b => ({
        id: crypto.randomUUID(),
        nid: null,
        type: b.type,
        data: b.data ?? {},
        hiddenOn: b.hiddenOn ?? [],
      })),
    }

    const res = await fetch('/api/builder/save', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(pageData),
    })

    if (!res.ok) {
      throw new Error(`Page creation error: ${res.status}`)
    }

    // Le slug RENDU fait foi : le back a pu le suffixer pour le rendre unique.
    // Rediriger sur celui qu'on avait deviné ouvrirait la page de quelqu'un
    // d'autre — ou un 404.
    const created = await res.json().catch(() => null)
    const createdData = created?.data ?? created ?? {}
    const finalSlug = createdData.slug || slug

    // Mise à jour optimiste de la liste locale
    const bucket = isTemplate ? setTemplates : setPages
    bucket(prev => (prev.find(p => p.slug === finalSlug) ? prev : [...prev, { slug: finalSlug, title }]))

    return finalSlug
  }, [])

  /**
   * Duplique une page existante via POST /api/builder/page/duplicate.
   *
   * C'est AUSSI le geste « créer une page à partir d'un modèle » : un modèle
   * n'est qu'une page, et sa copie est une page ordinaire. Ce qui distingue les
   * deux résultats se joue dans les BLOCS, côté serveur : un bloc ordinaire est
   * copié (indépendant), un bloc lié (`SharedBlock`) garde sa référence — donc
   * un header posé en « bloc existant » dans le modèle reste commun à toutes
   * les pages qui en descendent.
   *
   * @returns {string} slug de la page dupliquée
   */
  const duplicatePage = useCallback(async (sourceSlug, newTitle, targetSiteId = null, asTemplate = false) => {
    const res = await fetch('/api/builder/page/duplicate', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        sourceSlug,
        title: newTitle,
        asTemplate,
        // Absente ⇒ la copie hérite du site de l'originale (comportement
        // historique). Présente ⇒ elle naît là où l'éditeur travaille.
        ...(targetSiteId ? { siteId: Number(targetSiteId) } : {}),
      }),
    })

    const data = await res.json()
    if (!res.ok || !data.success) {
      throw new Error(data.message || `Duplication error: ${res.status}`)
    }

    const bucket = asTemplate ? setTemplates : setPages
    bucket(prev => (prev.find(p => p.slug === data.slug) ? prev : [...prev, { slug: data.slug, title: data.title }]))

    return data.slug
  }, [])

  /**
   * Crée une page — ou un modèle — DANS le répertoire du wiki (`directoryId`).
   *
   * `directory` voyage en paramètre d'URL et non dans le corps : c'est là que
   * `orbit_doc_node_create_access()` le lit pour statuer sur
   * `can_create_children` du répertoire visé. `template_source` déclenche le
   * clonage du modèle au presave (résumé, catégorie, étiquettes, icône et
   * surtout les BLOCS) — la copie est faite par Drupal, jamais rejouée ici.
   *
   * @returns {number} le nid de la page créée, dont se déduit `/docs/page/<nid>`.
   */
  const createInDirectory = useCallback(async (title, templateNid = null, asTemplate = false) => {
    const result = await saveDocPage({
      title: title.trim() || 'Nouvelle page',
      directoryId,
      isTemplate: asTemplate,
      ...(templateNid ? { templateSource: templateNid } : {}),
    })
    // L'id d'une entité créée revient à la RACINE de la réponse du save
    // decoupled, jamais dans `data`.
    const nid = result?.data?.id || result?.id
    if (!nid) {
      throw new Error('La page a été créée mais son identifiant est introuvable. Rechargez l’écran.')
    }
    return nid
  }, [directoryId])

  return { pages, templates, loading, error, createPage, duplicatePage, createInDirectory, reload: loadPages }
}

/**
 * Le MODÈLE d'un type de contenu : la page qui en rend les contenus
 * (`modelFor`), avec son chemin public et son mapping. La règle est 1 type =
 * 1 modèle ; une LISTE est rendue quand même, parce que des déclarations
 * antérieures à la règle peuvent en porter plusieurs — l'onglet « Modèle » les
 * montre pour qu'on tranche, au lieu d'en choisir une en silence.
 *
 * C'est la liste que propose le sélecteur de `BuilderObjectEditor` : éditer une
 * instance « dans » l'une d'elles, c'est la monter en lunette au-dessus de
 * l'objet. Le back filtre (`?modelFor=<type>`, opt-in) et n'y met que les
 * entrées du type demandé — une page gabarit de deux types n'apporte ici que
 * celle qui nous concerne.
 *
 * Une page peut déclarer plusieurs URLs pour un même type ; c'est la PREMIÈRE
 * qui compte pour nous — le mapping y est le même, et l'écran n'a pas à choisir
 * une adresse publique pour éditer.
 */
export const useBuilderModelsFor = (objectType) => {
  const [models, setModels] = useState([])
  const [loading, setLoading] = useState(true)
  // Relecture à la demande — l'onglet « Modèle » vient de poser, détacher ou
  // modifier le modèle, et l'écran doit refléter ce que le serveur a retenu.
  const [version, setVersion] = useState(0)
  const reload = useCallback(() => setVersion(v => v + 1), [])

  useEffect(() => {
    if (!objectType) {
      setModels([])
      setLoading(false)
      return
    }
    let cancelled = false
    setLoading(true)
    fetch(`/api/builder/pages?modelFor=${encodeURIComponent(objectType)}`, { credentials: 'include' })
      .then(res => (res.ok ? res.json() : []))
      .then((rows) => {
        if (cancelled) return
        setModels((Array.isArray(rows) ? rows : []).map((row) => {
          const entry = Array.isArray(row.modelFor) ? row.modelFor[0] : null
          return {
            nid: row.nid,
            slug: row.slug,
            title: row.title,
            path: entry?.path ?? '',
            mapping: entry?.mapping ?? null,
            // Un GABARIT n'est servi nulle part : s'il porte un `modelFor`
            // (héritage d'avant la séparation des deux notions), les contenus
            // de ce type n'ont pas de vue publique. L'onglet « Modèle » le dit.
            isTemplate: row.isTemplate === true,
          }
        }))
      })
      .catch(() => { if (!cancelled) setModels([]) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [objectType, version])

  return { models, loading, reload }
}
